const emptyGuid = "00000000-0000-0000-0000-000000000000";
const guidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const isValidGuid = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized !== "" && normalized !== emptyGuid && guidPattern.test(normalized);
};

export const hasGuid = (...values) => values.some(isValidGuid);

const guidEquals = (a, b) =>
  isValidGuid(a) &&
  isValidGuid(b) &&
  String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/** Foreign key válida: código visível + id persistido */
export const hasForeignKey = (data, codeKey, idKeys) =>
  hasValue(data?.[codeKey]) && hasGuid(...idKeys.map((key) => data?.[key]));

export const getCompanyOptionsFromSession = () => {
  try {
    const raw = sessionStorage.getItem("companies");
    if (!raw) return [];
    const companies = JSON.parse(raw);
    if (!Array.isArray(companies)) return [];
    return companies.map((field) => ({
      label: field.Name,
      value: field.AccountId,
      ...field,
    }));
  } catch {
    return [];
  }
};

const getCompanyOptions = (data) =>
  data?.companyOptions?.length ? data.companyOptions : getCompanyOptionsFromSession();

/** UserCompany.Id a partir do estado ou das opções de company */
export const resolveCompanyUserId = (data) => {
  if (hasGuid(data?.userCompanyId)) {
    return String(data.userCompanyId).trim();
  }
  if (hasGuid(data?.UserCompanyId)) {
    return String(data.UserCompanyId).trim();
  }

  const options = getCompanyOptions(data);
  const normalizedCompany = String(data?.company ?? data?.clientName ?? "").trim().toLowerCase();
  const normalizedClientCode = String(data?.clientCode ?? "").trim().toLowerCase();

  const match = options.find(
    (c) =>
      guidEquals(c.Id, data?.userCompanyId) ||
      guidEquals(c.value, data?.accountId) ||
      guidEquals(c.AccountId, data?.accountId) ||
      guidEquals(c.AccountId, data?.clientId) ||
      guidEquals(c.value, data?.clientId) ||
      (normalizedCompany &&
        (String(c.label ?? c.Name ?? "").trim().toLowerCase() === normalizedCompany ||
          String(c.Code ?? "").trim().toLowerCase() === normalizedCompany)) ||
      (normalizedClientCode &&
        (String(c.Code ?? "").trim().toLowerCase() === normalizedClientCode ||
          String(c.label ?? c.Name ?? "").trim().toLowerCase() === normalizedClientCode)),
  );

  if (match?.Id && isValidGuid(match.Id)) {
    return String(match.Id).trim();
  }

  if (options.length === 1 && isValidGuid(options[0].Id)) {
    return String(options[0].Id).trim();
  }

  return "";
};

/** Normaliza company/accountId a partir das opções disponíveis */
export const resolveCompanyState = (data) => {
  const companyOptions = getCompanyOptions(data);
  const userCompanyId = resolveCompanyUserId({ ...data, companyOptions });
  const match = companyOptions.find((c) => guidEquals(c.Id, userCompanyId));

  const companyLabel =
    data?.company || data?.clientName || match?.label || match?.Name || "";

  return {
    ...data,
    companyOptions,
    userCompanyId: userCompanyId || data?.userCompanyId || "",
    company: companyLabel,
    clientName: data?.clientName || companyLabel,
    accountId: data?.accountId || match?.value || match?.AccountId || "",
  };
};

export const hasCompany = (data) => {
  const state = resolveCompanyState(data);
  const userCompanyId = resolveCompanyUserId(state);

  if (!hasGuid(userCompanyId)) return false;

  return (
    hasValue(state.company) ||
    hasValue(state.clientCode) ||
    hasValue(state.clientName) ||
    hasValue(state.accountId)
  );
};

export const hasCurrency = (data) =>
  hasForeignKey(data, "currencyCode", ["currencyId", "CurrencyId"]);

export const hasPort = (data, codeKey, idKeys) => hasForeignKey(data, codeKey, idKeys);

export const normalizeGuidForApi = (value) =>
  hasGuid(value) ? String(value).trim() : emptyGuid;

export const normalizeOptionalGuidForApi = (value) =>
  hasGuid(value) ? String(value).trim() : null;

export const buildQuotePayload = (data, { status, goodsDescription } = {}) => {
  const {
    Goods,
    Equipments,
    charges,
    companyOptions,
    company,
    accountId,
    clientId,
    GoodsCounter,
    EquipmentCounter,
    counterCharges,
    isMultiEdit,
    totalCurrency,
    ...quote
  } = data;

  const resolvedUserCompanyId = resolveCompanyUserId({
    ...data,
    companyOptions: companyOptions ?? getCompanyOptionsFromSession(),
  });
  const userCompanyId = hasGuid(resolvedUserCompanyId)
    ? resolvedUserCompanyId
    : hasGuid(quote.userCompanyId)
      ? String(quote.userCompanyId).trim()
      : hasGuid(quote.UserCompanyId)
        ? String(quote.UserCompanyId).trim()
        : null;

  return {
    ...quote,
    userCompanyId,
    UserCompanyId: userCompanyId,
    ...(status !== undefined ? { Status: status } : {}),
    ...(goodsDescription !== undefined ? { goodsDescription } : {}),
  };
};

/** Descrição no formulário ou em cada linha da tabela Goods */
export const resolveQuoteGoodsDescription = (data) => {
  if (hasValue(data?.goodsDescription)) {
    return String(data.goodsDescription).trim();
  }

  const goods = data?.Goods || [];
  const described = goods.find((item) => hasValue(item?.goodsDescription));
  return described ? String(described.goodsDescription).trim() : "";
};

export const hasGoodsDescription = (data) => {
  if (hasValue(data?.goodsDescription)) return true;

  const goods = data?.Goods || [];
  if (goods.length === 0) return false;

  return goods.every((item) => hasValue(item?.goodsDescription));
};

/** Repõe linha na tabela quando só existe descrição ao nível do quote */
export const hydrateGoodsFromQuoteDescription = (goods, quote) => {
  if ((goods?.length ?? 0) > 0 || !hasValue(quote?.goodsDescription)) {
    return goods ?? [];
  }

  return [{
    Id: 1,
    goodsDescription: quote.goodsDescription,
    commodityCode: "",
    commodityName: "",
    dangerousGoodsCode: "",
    grossWeight: "",
    measures: "",
  }];
};

export const normalizeQuoteEquipmentsForSave = (equipments = []) =>
  equipments.map(({ Id, id, equipmentType, ...rest }) => {
    const rawEquipmentId = rest.quoteEquipmentId ?? id ?? Id;

    return {
      ...rest,
      quoteEquipmentId: isValidGuid(rawEquipmentId)
        ? String(rawEquipmentId).trim()
        : null,
    };
  });

/** API exige decimal em GrossWeight; vazio/null → 0 */
export const normalizeQuoteGoodsForSave = (goods = []) =>
  goods
    .map(({ Id, id, ...rest }) => {
      const rawGoodsId = rest.quoteGoodsId ?? id ?? Id;

      return {
        ...rest,
        quoteGoodsId: isValidGuid(rawGoodsId) ? String(rawGoodsId).trim() : null,
        dangerousGoodsId: normalizeOptionalGuidForApi(rest.dangerousGoodsId),
        commodityId: normalizeGuidForApi(rest.commodityId),
        grossWeight:
          rest.grossWeight !== "" &&
          rest.grossWeight != null &&
          !Number.isNaN(Number(rest.grossWeight))
            ? Number(rest.grossWeight)
            : 0,
      };
    })
    .filter((item) => isValidGuid(item.commodityId));
